import {
    CalendarClock,
    Coins,
    Construction,
    Globe,
    Infinity as InfinityIcon,
    Link2,
    Server as ServerIcon,
    ShieldCheck,
} from "lucide-react"
import React from "react"
import { Link } from "react-router-dom"
import Layout from "../components/Layout"
import { Badge, Button, Card, CardHeader, Input } from "../components/ui"

/**
 * Servidores (ativações ilimitadas por DNS): recurso EM DESENVOLVIMENTO.
 * A tela fica inativa e explica o funcionamento planejado. A tela antiga era
 * um mock com dados falsos, o que confundia mais do que ajudava.
 */

const steps = [
    {
        icon: Globe,
        title: "Cadastre o DNS do servidor",
        text: "Informe o domínio do seu servidor de playlist (por exemplo, play.seuservidor.com). O painel passa a reconhecer todo link que aponte para ele.",
    },
    {
        icon: CalendarClock,
        title: "Assine o plano do servidor",
        text: "Um PIX por período (mensal) mantém o servidor ativo. Enquanto o plano estiver válido, não há cobrança por acesso.",
    },
    {
        icon: InfinityIcon,
        title: "Ativações ilimitadas",
        text: "Qualquer acesso (senha → link) cujo link aponte para esse DNS fica ativo automaticamente, sem gastar créditos. Novos clientes entram na hora.",
    },
]

const comparison = [
    {
        icon: Coins,
        title: "Créditos (disponível hoje)",
        text: "Você paga por acesso: cada ativação ou renovação consome 1 crédito da carteira. Indicado para bases pequenas ou vários servidores.",
        to: "/credits",
        cta: "Ir para a Carteira",
    },
    {
        icon: ServerIcon,
        title: "Servidor (em desenvolvimento)",
        text: "Você paga pelo servidor: um plano fixo cobre todos os acessos apontados para o DNS cadastrado, sem limite de dispositivos.",
    },
]

const ServersPage: React.FC = () => (
    <Layout title="Servidores">
        <div className="flex flex-col gap-6">
            {/* Aviso de status */}
            <Card className="flex flex-col gap-4 border-warning/40 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-4">
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-warning-soft text-warning-text">
                        <Construction className="h-5 w-5" />
                    </span>
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-lg font-semibold text-text">
                                Recurso em desenvolvimento
                            </h3>
                            <Badge variant="warning">Em breve</Badge>
                        </div>
                        <p className="mt-1 max-w-2xl text-sm text-muted">
                            Os servidores com ativações ilimitadas ainda não
                            estão disponíveis. Esta tela descreve como o
                            recurso vai funcionar. Enquanto isso, as ativações
                            continuam pela Carteira de créditos.
                        </p>
                    </div>
                </div>
                <Link to="/credits" className="shrink-0">
                    <Button variant="outline" leftIcon={<Coins size={16} />}>
                        Usar créditos
                    </Button>
                </Link>
            </Card>

            {/* Como vai funcionar */}
            <Card>
                <CardHeader
                    title="Como vai funcionar"
                    description="Um servidor é o DNS da sua playlist. Com o plano do servidor ativo, todos os acessos que apontam para ele ficam liberados."
                />
                <ol className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                    {steps.map(({ icon: Icon, title, text }, i) => (
                        <li
                            key={title}
                            className="flex flex-col gap-3 rounded-xl border border-border bg-elevated/40 p-4"
                        >
                            <div className="flex items-center gap-3">
                                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
                                    <Icon className="h-4 w-4" />
                                </span>
                                <span className="text-xs font-semibold uppercase tracking-wide text-subtle">
                                    Passo {i + 1}
                                </span>
                            </div>
                            <h4 className="text-sm font-semibold text-text">
                                {title}
                            </h4>
                            <p className="text-sm text-muted">{text}</p>
                        </li>
                    ))}
                </ol>
            </Card>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Créditos vs servidor */}
                <Card>
                    <CardHeader
                        title="Créditos ou servidor?"
                        description="Os dois modelos vão coexistir. Você escolhe por servidor."
                    />
                    <div className="mt-5 flex flex-col gap-3">
                        {comparison.map(({ icon: Icon, title, text, to, cta }) => (
                            <div
                                key={title}
                                className="flex items-start gap-3 rounded-xl border border-border p-4"
                            >
                                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-elevated text-primary">
                                    <Icon className="h-4 w-4" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <h4 className="text-sm font-semibold text-text">
                                        {title}
                                    </h4>
                                    <p className="mt-1 text-sm text-muted">
                                        {text}
                                    </p>
                                    {to && cta && (
                                        <Link
                                            to={to}
                                            className="mt-2 inline-block text-sm font-medium text-primary hover:underline"
                                        >
                                            {cta}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Prévia inativa do cadastro */}
                <Card className="relative overflow-hidden">
                    <CardHeader
                        title="Prévia do cadastro"
                        description="Assim será o formulário. Inativo por enquanto."
                    />
                    <fieldset
                        disabled
                        aria-disabled="true"
                        className="mt-5 flex flex-col gap-3 opacity-60"
                    >
                        <label className="text-xs font-medium text-muted">
                            DNS do servidor
                        </label>
                        <div className="relative">
                            <Link2
                                size={16}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle"
                            />
                            <Input
                                placeholder="play.seuservidor.com"
                                className="pl-9 font-mono"
                                readOnly
                            />
                        </div>
                        <div className="flex flex-col gap-2 rounded-lg border border-border bg-elevated/40 p-3 text-sm text-muted sm:flex-row sm:items-center sm:justify-between">
                            <span className="inline-flex items-center gap-2">
                                <ShieldCheck size={16} className="text-primary" />
                                Plano mensal · ativações ilimitadas
                            </span>
                            <Button type="button" leftIcon={<ServerIcon size={16} />}>
                                Cadastrar servidor
                            </Button>
                        </div>
                    </fieldset>
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-4">
                        <Badge variant="warning">Disponível em breve</Badge>
                    </div>
                </Card>
            </div>
        </div>
    </Layout>
)

export default ServersPage
